import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogBody,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useFetch } from '@/hooks/useFecth';
import server from '@/services/server';
import useClubErrors from '../hooks/useClubErrors';
import { formatDate } from '../utils/subscriberDates';

const OPTION_COPY = {
    WITHDRAWAL: {
        title: 'Arrependimento (7 dias): estorno total',
        description: 'Devolve tudo o que foi pago no ciclo e encerra o acesso na hora.',
    },
    NONE: {
        title: 'Cancelar sem estorno',
        description: 'Acesso mantido até o fim do ciclo pago. Depois a assinatura fica inativa.',
    },
    TOTAL: {
        title: 'Estorno total',
        description: 'Devolve tudo o que foi pago no ciclo e encerra o acesso na hora.',
    },
    PARTIAL: {
        title: 'Estorno parcial',
        description: 'Devolve parte do valor. Você escolhe se o acesso encerra agora ou vai até uma data.',
    },
};

const REASON_ITEMS = { CLUB: 'Decisão do clube', CUSTOMER: 'Pedido do assinante' };

function formatBRL(value) {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function toDateInput(value) {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : format(date, 'yyyy-MM-dd');
}

function OptionCard({ option, selected, onSelect, extra }) {
    const copy = OPTION_COPY[option];
    return (
        <button
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onSelect(option)}
            className={cn(
                'flex w-full flex-col gap-1 rounded-lg border p-3 text-left transition-colors',
                selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/50',
            )}
        >
            <span className="text-sm font-medium">{copy.title}</span>
            <span className="text-xs text-muted-foreground">{copy.description}</span>
            {extra ? <span className="text-xs text-muted-foreground">{extra}</span> : null}
        </button>
    );
}

/** Dialog único de cancelamento do assinante (com ou sem estorno, arrependimento de 7 dias). */
export default function CancelSubscriptionDialog({ open, onOpenChange, personId, personName, onDone }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const { data: preview, isLoading } = useFetch(
        open && personId ? `club/subscribers/${personId}/subscription/cancel-preview` : null,
    );

    const [option, setOption] = useState('NONE');
    const [value, setValue] = useState('');
    const [access, setAccess] = useState('END_NOW');
    const [accessUntil, setAccessUntil] = useState('');
    const [reason, setReason] = useState('CLUB');
    const [note, setNote] = useState('');
    const [manualRefund, setManualRefund] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open || !preview) return;
        setOption(preview.defaultOption || 'NONE');
        setValue(preview.suggestedPartial ? String(preview.suggestedPartial.toFixed(2)) : '');
        setAccess('END_NOW');
        setAccessUntil(toDateInput(preview.cycleEnd));
        setReason('CLUB');
        setNote('');
        setManualRefund(false);
    }, [open, preview]);

    const options = useMemo(() => (Array.isArray(preview?.options) ? preview.options : []), [preview]);
    const isInstallment = preview?.billingModel === 'INSTALLMENT';
    const isRefund = option !== 'NONE';
    const needsManualRefund = isRefund && preview?.boletoManual;
    const partialValue = Number(String(value).replace(',', '.'));
    const partialInvalid =
        option === 'PARTIAL' && (!(partialValue > 0) || partialValue > Number(preview?.paidInCycle || 0) + 0.001);
    const keepUntilInvalid = option === 'PARTIAL' && access === 'KEEP_UNTIL' && !accessUntil;
    const canSubmit =
        !submitting && !isLoading && preview && options.includes(option) && !partialInvalid && !keepUntilInvalid && (!needsManualRefund || manualRefund);

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const payload = {
                option,
                reason,
                note: note.trim() || null,
                manualRefund: Boolean(needsManualRefund && manualRefund),
                ...(option === 'PARTIAL'
                    ? {
                        value: partialValue,
                        access,
                        accessUntil: access === 'KEEP_UNTIL' ? `${accessUntil}T23:59:59` : null,
                    }
                    : {}),
            };
            const { data: body } = await server.post(`club/subscribers/${personId}/subscription/cancel`, payload);
            enqueueSnackbar(body?.message || 'Assinatura cancelada.');
            onOpenChange(false);
            await onDone?.();
        } catch (error) {
            setErrors(error);
        } finally {
            setSubmitting(false);
        }
    };

    const optionExtra = opt => {
        if (!preview) return null;
        if (opt === 'WITHDRAWAL' && preview.withdrawalEndsAt) {
            return `Prazo até ${formatDate(preview.withdrawalEndsAt)}. Valor: ${formatBRL(preview.paidInCycle)}.`;
        }
        if (opt === 'TOTAL') return `Valor: ${formatBRL(preview.paidInCycle)}.`;
        if (opt === 'NONE' && preview.cycleEnd && preview.onlineBilling) {
            return `Acesso até ${formatDate(preview.cycleEnd)}.${isInstallment ? ' As parcelas seguem no cartão do assinante.' : ''}`;
        }
        return null;
    };

    return (
        <Dialog open={open} onOpenChange={isOpen => !submitting && onOpenChange(isOpen)}>
            <ScrollDialogContent className="sm:max-w-xl">
                <ScrollDialogHeader>
                    <DialogTitle>Cancelar assinatura</DialogTitle>
                    {personName ? <p className="text-sm text-muted-foreground">{personName}</p> : null}
                </ScrollDialogHeader>

                <DialogBody>
                    {isLoading || !preview ? (
                        <div className="flex flex-col gap-3">
                            <Skeleton className="h-16 w-full" />
                            <Skeleton className="h-16 w-full" />
                            <Skeleton className="h-16 w-full" />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-4">
                            <div className="grid grid-cols-2 gap-2 rounded-lg border bg-muted/30 p-3 text-sm">
                                <span className="text-muted-foreground">Cobrança</span>
                                <span className="text-right">
                                    {!preview.onlineBilling
                                        ? 'Sem cobrança online'
                                        : isInstallment
                                          ? `Anual parcelado ${preview.installment?.installmentCount || ''}x`
                                          : preview.cycle === 'YEARLY'
                                            ? 'Anual recorrente'
                                            : 'Mensal recorrente'}
                                </span>
                                {preview.onlineBilling ? (
                                    <>
                                        <span className="text-muted-foreground">Pago no ciclo</span>
                                        <span className="text-right tabular-nums">{formatBRL(preview.paidInCycle)}</span>
                                    </>
                                ) : null}
                                {preview.cycleEnd ? (
                                    <>
                                        <span className="text-muted-foreground">Fim do ciclo</span>
                                        <span className="text-right">{formatDate(preview.cycleEnd)}</span>
                                    </>
                                ) : null}
                            </div>

                            {preview.inWithdrawalWindow ? (
                                <Alert>
                                    <AlertTitle>Dentro do prazo de arrependimento</AlertTitle>
                                    <AlertDescription>
                                        Compra feita pelo site há menos de 7 dias (até{' '}
                                        {formatDate(preview.withdrawalEndsAt)}). Se o assinante pediu para desistir, o
                                        estorno é total.
                                    </AlertDescription>
                                </Alert>
                            ) : null}

                            {preview.scheduled ? (
                                <Alert>
                                    <AlertTitle>Troca de plano agendada</AlertTitle>
                                    <AlertDescription>
                                        Há uma compra do próximo ciclo
                                        {preview.scheduled.startAt
                                            ? ` com início em ${formatDate(preview.scheduled.startAt)}`
                                            : ''}
                                        . Ela também será cancelada
                                        {preview.scheduled.billingModel === 'INSTALLMENT'
                                            ? ', com estorno total do parcelamento agendado'
                                            : ''}
                                        .
                                    </AlertDescription>
                                </Alert>
                            ) : null}

                            {!options.length ? (
                                <p className="text-sm text-muted-foreground">
                                    Nada a fazer: a assinatura já está encerrada ou não há valor pago no ciclo para
                                    estornar.
                                </p>
                            ) : null}

                            <div role="radiogroup" className="flex flex-col gap-2">
                                {options.map(opt => (
                                    <OptionCard
                                        key={opt}
                                        option={opt}
                                        selected={option === opt}
                                        onSelect={setOption}
                                        extra={optionExtra(opt)}
                                    />
                                ))}
                            </div>

                            {preview.onlineBilling && !isInstallment && preview.cycle !== 'YEARLY' && !preview.inWithdrawalWindow ? (
                                <p className="text-xs text-muted-foreground">
                                    Plano mensal fora do prazo de 7 dias não tem estorno: o acesso vai até o fim do mês
                                    pago.
                                </p>
                            ) : null}

                            {option === 'PARTIAL' ? (
                                <div className="flex flex-col gap-3 rounded-lg border p-3">
                                    <Field>
                                        <FieldLabel htmlFor="cancel-partial-value">Valor do estorno</FieldLabel>
                                        <Input
                                            id="cancel-partial-value"
                                            inputMode="decimal"
                                            value={value}
                                            onChange={event => setValue(event.target.value)}
                                            aria-invalid={partialInvalid || undefined}
                                        />
                                        <FieldDescription>
                                            Sugerido: {formatBRL(preview.suggestedPartial)} ({preview.remainingDays} de{' '}
                                            {preview.totalDays} dias restantes). Máximo {formatBRL(preview.paidInCycle)}.
                                            {isInstallment
                                                ? ' O Asaas abate o valor das primeiras parcelas.'
                                                : ''}
                                        </FieldDescription>
                                    </Field>
                                    <Field>
                                        <FieldLabel>Acesso do assinante</FieldLabel>
                                        <Select value={access} onValueChange={setAccess}>
                                            <SelectTrigger>
                                                <SelectValue>
                                                    {access === 'KEEP_UNTIL' ? 'Manter até uma data' : 'Encerrar agora'}
                                                </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="END_NOW">Encerrar agora</SelectItem>
                                                <SelectItem value="KEEP_UNTIL">Manter até uma data</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    {access === 'KEEP_UNTIL' ? (
                                        <Field>
                                            <FieldLabel htmlFor="cancel-access-until">Acesso até</FieldLabel>
                                            <Input
                                                id="cancel-access-until"
                                                type="date"
                                                value={accessUntil}
                                                min={toDateInput(new Date())}
                                                max={toDateInput(preview.cycleEnd) || undefined}
                                                onChange={event => setAccessUntil(event.target.value)}
                                            />
                                        </Field>
                                    ) : null}
                                </div>
                            ) : null}

                            {needsManualRefund ? (
                                <Alert variant="error">
                                    <AlertTitle>Boleto: estorno manual</AlertTitle>
                                    <AlertDescription className="flex flex-col gap-2">
                                        <span>
                                            O Asaas não estorna boleto. Faça a devolução por transferência ao assinante e
                                            registre aqui.
                                        </span>
                                        <label className="flex items-center gap-2 text-sm">
                                            <Checkbox checked={manualRefund} onCheckedChange={v => setManualRefund(Boolean(v))} />
                                            Já devolvi o valor por transferência
                                        </label>
                                    </AlertDescription>
                                </Alert>
                            ) : null}

                            {option !== 'WITHDRAWAL' && preview.onlineBilling ? (
                                <Field>
                                    <FieldLabel>Motivo</FieldLabel>
                                    <Select value={reason} onValueChange={setReason}>
                                        <SelectTrigger>
                                            <SelectValue>{REASON_ITEMS[reason]}</SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(REASON_ITEMS).map(([key, label]) => (
                                                <SelectItem key={key} value={key}>
                                                    {label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                            ) : null}

                            <Field>
                                <FieldLabel htmlFor="cancel-note">Observação (opcional)</FieldLabel>
                                <Textarea
                                    id="cancel-note"
                                    rows={2}
                                    maxLength={500}
                                    value={note}
                                    onChange={event => setNote(event.target.value)}
                                />
                            </Field>
                        </div>
                    )}
                </DialogBody>

                <ScrollDialogFooter>
                    <Button type="button" variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
                        Voltar
                    </Button>
                    <Button type="button" variant="destructive" disabled={!canSubmit} onClick={handleSubmit}>
                        {submitting ? <Spinner className="size-4" /> : null}
                        {isRefund ? 'Cancelar e estornar' : 'Cancelar assinatura'}
                    </Button>
                </ScrollDialogFooter>
            </ScrollDialogContent>
        </Dialog>
    );
}
